import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ROLES, type Role } from "@amber/contracts";
import { Button, Callout, Mark, Skeleton, isApiError, useAppNavigate, useCsrf } from "@amber/ui";
import { ROLE_LABELS, type SignInRefusal } from "../contracts/index";
import { devSignIn, sessionQuery, signInOptionsQuery } from "./session";
import type { SignInSearch } from "./search";

function GoogleSignIn({ pending, google, href }: { pending: boolean; google: boolean; href: string }) {
  if (pending) return <Skeleton className="h-9 w-full" />;
  if (google) {
    return (
      <a
        href={href}
        className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-primary bg-primary font-strong text-on-primary no-underline hover:brightness-110"
      >
        Sign in with Google
      </a>
    );
  }
  return (
    <Button className="h-9 w-full" disabledReason="Google sign-in isn't configured on this server">
      Sign in with Google
    </Button>
  );
}

function refusalText(refusal: SignInRefusal, invited: string | undefined): { title: string; body: string } {
  switch (refusal) {
    case "invitation_only":
      return {
        title: "This workspace is by invitation only",
        body: "Your Google account has no access yet. Ask an Admin for an invitation link, then sign in again.",
      };
    case "wrong_account":
      return {
        title: "This invitation is for a different address",
        body: invited
          ? `The invitation was sent to ${invited}. Sign in with that Google account.`
          : "Sign in with the Google account the invitation was sent to.",
      };
    case "deactivated":
      return {
        title: "Your account is deactivated",
        body: "Ask an Admin to reactivate it. Your earlier work is kept.",
      };
    case "unverified_email":
      return {
        title: "Google hasn't verified this address",
        body: "Only verified Google Workspace addresses can sign in.",
      };
    case "invitation_expired":
      return { title: "This invitation has expired", body: "Invitations last 14 days. Ask an Admin for a new link." };
    case "failed":
      return { title: "Sign-in didn't complete", body: "Nothing was changed. Try again." };
  }
}

function safeReturnTo(value: string | undefined): string {
  return value?.startsWith("/") && !value.startsWith("//") && !value.startsWith("/sign-in") ? value : "/";
}

export function SignInPage({ search }: { search: SignInSearch }) {
  const options = useQuery(signInOptionsQuery);
  const client = useQueryClient();
  const navigate = useAppNavigate();
  const csrf = useCsrf();
  const [pending, setPending] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);
  const returnTo = safeReturnTo(search.return_to);
  const refusal = search.refused ? refusalText(search.refused, search.invited) : null;

  const params = new URLSearchParams({ return_to: returnTo });
  if (search.invitation) params.set("invitation", search.invitation);
  const googleHref = `/api/auth/sign-in?${params.toString()}`;

  const signInAs = async (role: Role) => {
    setPending(role);
    setError(null);
    try {
      const token =
        csrf() ??
        (await client.query(sessionQuery).then(
          (session) => session.csrf_token,
          () => null,
        ));
      await devSignIn(role, token);
      await client.resetQueries({ queryKey: sessionQuery.queryKey });
      navigate(returnTo);
    } catch (e) {
      setError(isApiError(e) ? e.message : "Sign-in didn't complete. Try again.");
      setPending(null);
    }
  };

  return (
    <main id="main" className="grid min-h-full place-items-center bg-surface px-4 py-10">
      <div className="w-full max-w-[400px] rounded-2xl border border-divider bg-bg px-7 pt-8 pb-7 shadow-pop">
        <div className="flex flex-col items-center text-center">
          <Mark filled className="size-11" />
          <h1 className="m-0 mt-3 text-2xl font-semibold">Amber</h1>
          <p className="m-0 mt-1 text-muted">Archive of what public figures said</p>
        </div>

        {search.signed_out && !refusal ? (
          <Callout role="status" className="mt-5">
            You are signed out.
          </Callout>
        ) : null}
        {refusal ? (
          <Callout tone="attention" role="alert" title={refusal.title} className="mt-5">
            {refusal.body}
          </Callout>
        ) : null}
        {search.invitation && !refusal ? (
          <Callout tone="info" className="mt-5" title="You've been invited">
            Sign in with Google using the address the invitation was sent to.
          </Callout>
        ) : null}

        <div className="mt-6">
          <GoogleSignIn pending={options.isPending} google={options.data?.google === true} href={googleHref} />
        </div>

        {options.data?.dev_sign_in ? (
          <section aria-labelledby="dev-sign-in" className="mt-6 border-t border-divider pt-4">
            <h2 id="dev-sign-in" className="m-0 text-md font-semibold text-muted uppercase tracking-[0.04em]">
              Development sign-in
            </h2>
            <p className="m-0 mt-1 text-md text-muted">
              Signs in a local test user with the chosen role. Not available in production.
            </p>
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              {ROLES.map((role) => (
                <Button
                  key={role}
                  size="sm"
                  aria-busy={pending === role || undefined}
                  onClick={() => {
                    void signInAs(role);
                  }}
                >
                  {ROLE_LABELS[role]}
                </Button>
              ))}
            </div>
            {error ? (
              <Callout tone="attention" role="alert" className="mt-3">
                {error}
              </Callout>
            ) : null}
          </section>
        ) : null}
      </div>
    </main>
  );
}
